import type { TableOrderInterface } from '@uefadigital/panenka';
import {
  PkTable,
  PkTableBody,
  PkTableCell,
  PkTableHeader,
  PkTableHeaderCol,
  PkTableRow,
} from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useEffect, useMemo, useState } from 'react';

import PlayerIdentifier from '@shared/Identifier/PlayerIdentifier';
import { useTranslateFn } from '@shared/Translate';
import { Order } from '@shared-types/common.types';
import type { Statistic, StatisticDefinition } from '@shared-types/stats.types';
import { orderBy } from '@utilities/collection-utils';
import { getStatNumericValue, getStatValue } from '@utilities/stats';

import * as styles from './styles.module.scss';

export const TableComparison = ({
  stats,
  statDefs,
  seasons,
  isStory,
}: {
  stats: Statistic[];
  statDefs: StatisticDefinition[];
  seasons: number[];
  isStory?: boolean;
}) => {
  const t = useTranslateFn();

  const [sorting, setSorting] = useState<StatisticDefinition>(statDefs?.[0]);
  const [order, setOrder] = useState<Order>(Order.DESC);

  const types = useMemo(() => {
    const defs = statDefs?.filter((t) => stats?.some((s) => getStatNumericValue(s, t) != null));

    if (defs?.length > 4) defs.length = 4;

    return defs;
  }, [statDefs, stats]);

  useEffect(() => {
    if (!types) return;
    setSorting(types[0]);
    setOrder(Order.DESC);
  }, [types]);

  const handleSortingChange = (event: CustomEvent<{ columnKey: string | number; order: TableOrderInterface }>) => {
    const statDef = types.find((sd) => sd.name === event.detail.columnKey);
    setSorting(statDef);
    setOrder(event.detail.order as Order);
  };

  const getHigherStat = (def: StatisticDefinition, stat: Statistic) => {
    const maxValue = Math.max(...stats.map((s) => getStatNumericValue(s, def)));
    return getStatNumericValue(stat, def) >= maxValue;
  };

  const sortedStats = useMemo(() => {
    if (!stats) return null;
    const sorted = orderBy(stats, (s) => getStatNumericValue(s, sorting));
    return order === Order.ASC ? sorted : sorted.reverse();
  }, [order, sorting, stats]);

  const getLinkActive = (index: number) => {
    const season = seasons?.[index];
    return !!season && season >= window.currentSeason;
  };

  if (!sortedStats?.length || !types?.length) return null;

  return (
    <div pk-theme={isStory ? 'dark' : 'light'} className={styles.container}>
      <PkTable
        onPkTableSortBy={handleSortingChange}
        sortBy={sorting?.name}
        sortOrder={order}
        firstCellMobileHeader
        cellVerticalPad={8}
        rowSpacing={16}
      >
        <PkTableHeader className={styles.header}>
          <PkTableHeaderCol width={230} className={styles.titleCell} columnKey='identifier'>
            <div className={styles.title}>{t('players_to_watch')}</div>
          </PkTableHeaderCol>
          {types.map((t) => (
            <PkTableHeaderCol key={`type_${t.name}`} sortable columnKey={t.name} aligned='center'>
              <span title={t.displayName}>{t.displayName}</span>
            </PkTableHeaderCol>
          ))}
        </PkTableHeader>
        <PkTableBody key={`stats_body_${stats.length}`}>
          {sortedStats?.map((stat, i) => (
            <PkTableRow className={styles.rounded} key={`stat_row_${stat.playerId}`}>
              <PkTableCell columnKey='identifier' withDivider>
                <PlayerIdentifier player={stat.player} team={stat.team} useFullName enableLink={getLinkActive(i)} />
              </PkTableCell>
              {types.map((t) => (
                <PkTableCell aligned='center' columnKey={t.name} key={`stat_${t.name}`}>
                  <div
                    className={clsx({
                      [styles.cell]: true,
                      [styles.cellHigher]: getHigherStat(t, stat),
                    })}
                  >
                    {getStatValue(stat, t)}
                  </div>
                </PkTableCell>
              ))}
            </PkTableRow>
          ))}
        </PkTableBody>
      </PkTable>
    </div>
  );
};

export default memo(TableComparison);
